import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";

import { ActionButtonComponent } from "@hermes/aphrodite/action-button";
import { CallToActionComponent } from "@hermes/aphrodite/call-to-action";
import { CarouselComponent } from "@hermes/aphrodite/carousel";
import {
    ExpansionPanelComponent,
    ExpansionPanelPartDirective,
} from "@hermes/aphrodite/expansion-panel";
import { MessageBlockComponent } from "@hermes/aphrodite/message-block";
import { PriceComponent } from "@hermes/aphrodite/price";
import { SeparatorComponent } from "@hermes/aphrodite/separator";
import { StickyWrapperComponent } from "@hermes/aphrodite/sticky-wrapper";
import {
    SvgIconComponent,
    svgPlayMedia,
    SvgRegistryService,
} from "@hermes/aphrodite/svg";
import { VideoComponent } from "@hermes/aphrodite/video";
import { ZoomCursorDirective } from "@hermes/aphrodite/zoom-cursor";
import {
    ApplePayModule,
    CallToActionApplePayComponent,
} from "@hermes/fragments/apple-pay";
import { CallToActionAddToCartModule } from "@hermes/fragments/call-to-action-add-to-cart";
import { CallToActionNotifyMeModule } from "@hermes/fragments/call-to-action-notify-me";
import {
    CallToActionPurchaseComponent,
    CallToActionPurchaseModule,
} from "@hermes/fragments/call-to-action-purchase";
import { CallToActionRegisterYourInterestModule } from "@hermes/fragments/call-to-action-register-your-interest";
import { ImageResizerModule } from "@hermes/fragments/image-resizer";
import { PersonalizeLabelComponent } from "@hermes/fragments/personalize-label";
import { SpaceSeparatorPipeModule } from "@hermes/fragments/product-utils";
import { VariantItemModule } from "@hermes/fragments/variant-item";
import { VtoCallToActionComponent } from "@hermes/fragments/vto-button";
import { AgecService } from "@hermes/pages/agec-page";
import { ProductInViewportService } from "@hermes/pages/grid-page";
import { ProductPageModule } from "@hermes/pages/product";
import { ApplePayStateModule } from "@hermes/states/apple-pay";
import { BackInStockStateModule } from "@hermes/states/back-in-stock";
import { FlipperStateModule } from "@hermes/states/flipper";
import { PaymentMethodsStateModule } from "@hermes/states/payment-methods";
import { ProductImageSeoUrlPipeModule } from "@hermes/states/product-page";
import { TrayStateModule } from "@hermes/states/tray";

import { DetectViewportDirectiveModule } from "@hermes/utils-generic/directives/detect-viewport";
import { SrOnlyNoSnippetDirective } from "@hermes/utils-generic/directives/sr-only-no-snippet";
import { I18nAssetAttributeDirectiveModule } from "@hermes/utils-generic/i18n-asset-attribute";
import { ImageUrlPipeModule } from "@hermes/utils-generic/pipes/image-url";
import { RouterServiceModule } from "@hermes/utils-generic/services/router";

import { AppleWatchImageAnimationsComponent } from "./components/apple-watch-image-animations/apple-watch-image-animations.component";
import { CallToActionFindInStoreComponent } from "./components/call-to-action-find-in-store/call-to-action-find-in-store.component";
import { ProductDoubleImageComponent } from "./components/product-double-image/product-double-image.component";
import { ProductImageComponent } from "./components/product-image/product-image.component";
import { ProductImageGalleryComponent } from "./components/product-image-gallery/product-image-gallery.component";
import { ProductListImageComponent } from "./components/product-list-image/product-list-image.component";
import { ProductVariantsComponent } from "./components/product-variants/product-variants.component";
import { ShowMoreComponent } from "./components/show-more/show-more.component";
import { TwoColumnsComponent } from "./components/two-columns/two-columns.component";
import { ProductListItemService } from "./services/product-list-item.service";

const COMPONENTS = [
    // shared by all templates
    TwoColumnsComponent,
    CallToActionFindInStoreComponent,
    // shared by several templates
    ProductImageComponent,
    ProductImageGalleryComponent,
    ProductVariantsComponent,
    ProductDoubleImageComponent,
    ShowMoreComponent,
    ProductListImageComponent,
    // bad dependancy from ProductImageComponent
    AppleWatchImageAnimationsComponent,
];

@NgModule({
    declarations: [...COMPONENTS],
    exports: [
        ...COMPONENTS,
        CallToActionPurchaseComponent,
        CallToActionApplePayComponent,
        ProductPageModule,
    ],
    providers: [AgecService, ProductListItemService, ProductInViewportService],
    imports: [
        ReactiveFormsModule,
        PriceComponent,
        ActionButtonComponent,
        CallToActionComponent,
        CarouselComponent,
        CommonModule,
        CallToActionAddToCartModule,
        CallToActionPurchaseModule,
        FlipperStateModule,
        I18nAssetAttributeDirectiveModule,
        MessageBlockComponent,
        PaymentMethodsStateModule,
        ProductImageSeoUrlPipeModule,
        RouterServiceModule,
        SeparatorComponent,
        StickyWrapperComponent,
        SvgIconComponent,
        ExpansionPanelComponent,
        ExpansionPanelPartDirective,
        TranslateModule,
        BackInStockStateModule,
        TrayStateModule,
        VideoComponent,
        ImageResizerModule,
        DetectViewportDirectiveModule,
        ApplePayModule,
        ApplePayStateModule,
        VariantItemModule,
        PersonalizeLabelComponent,
        // For ProductVariantItemComponent & ProductVariantsComponent
        ImageUrlPipeModule,
        ZoomCursorDirective,
        VtoCallToActionComponent,
        CallToActionNotifyMeModule,
        CallToActionRegisterYourInterestModule,
        SpaceSeparatorPipeModule,
        SrOnlyNoSnippetDirective,
        PaymentMethodsStateModule,
        ProductPageModule,
    ],
})
export class CommonProductPageModule {
    constructor(svgRegistryService: SvgRegistryService) {
        svgRegistryService.registerSvgs([svgPlayMedia]);
    }
}
